/**
 * Seed Chart of Accounts for a residential contractor (Koredor default).
 *
 * Structured for: cash/CC reconciliation, AR/AP subledgers, WIP by trade,
 * and trade-level revenue + COGS split (material vs labor) per Steve's spec.
 */
import type { GLAccount } from "./glTypes";

const acct = (
  number: string,
  name: string,
  type: GLAccount["type"],
  subtype: GLAccount["subtype"],
  normalBalance: GLAccount["normalBalance"],
  extras: Partial<GLAccount> = {},
): GLAccount => ({
  id: number,
  number,
  name,
  type,
  subtype,
  normalBalance,
  active: true,
  ...extras,
});

export const DEFAULT_TRADE_TYPES = ["Windows", "Flooring", "Bathroom", "Kitchen", "Roofing", "General"] as const;
export type TradeType = (typeof DEFAULT_TRADE_TYPES)[number];

/** Helper that produces 3 child accounts per trade: Revenue, Cost of Material, Cost of Labor. */
function tradeAccounts(): GLAccount[] {
  const out: GLAccount[] = [];
  DEFAULT_TRADE_TYPES.forEach((trade, i) => {
    const base = 4000 + (i + 1) * 10;
    out.push(
      acct(String(base), `Revenue — ${trade}`, "revenue", "revenue", "credit", {
        parentId: "4000",
        tradeType: trade,
      }),
    );
  });
  DEFAULT_TRADE_TYPES.forEach((trade, i) => {
    const base = 5000 + (i + 1) * 10;
    out.push(
      acct(String(base), `Cost of Material — ${trade}`, "expense", "cost_of_material", "debit", {
        parentId: "5000",
        tradeType: trade,
      }),
    );
  });
  DEFAULT_TRADE_TYPES.forEach((trade, i) => {
    const base = 5500 + (i + 1) * 10;
    out.push(
      acct(String(base), `Cost of Labor — ${trade}`, "expense", "cost_of_labor", "debit", {
        parentId: "5500",
        tradeType: trade,
      }),
    );
  });
  return out;
}

export const SEED_CHART_OF_ACCOUNTS: GLAccount[] = [
  // ───── Assets (1000s) ─────
  acct("1000", "Cash — Operating", "asset", "cash", "debit"),
  acct("1010", "Cash — Payroll", "asset", "cash", "debit"),
  acct("1020", "Cash — Savings / Reserve", "asset", "cash", "debit"),
  acct("1090", "Unreconciled Cash", "asset", "unreconciled_cash", "debit", {
    description: "Clearing account for bank-feed transactions awaiting match.",
  }),
  acct("1100", "Accounts Receivable", "asset", "accounts_receivable", "debit", {
    requiresSubledger: "customer",
  }),
  acct("1200", "Work In Progress", "asset", "wip", "debit", {
    requiresSubledger: "job",
    description: "Accumulated job costs (materials + labor + subs) for open jobs.",
  }),
  acct("1300", "Prepaid Expenses", "asset", "prepaid", "debit"),
  acct("1500", "Vehicles & Equipment", "asset", "fixed_asset", "debit"),
  acct("1590", "Accumulated Depreciation", "asset", "fixed_asset", "credit", {
    description: "Contra-asset. Credit balance offsets 1500 Vehicles & Equipment.",
  }),
  acct("1310", "Inventory On Hand", "asset", "other_asset", "debit", {
    description: "Warehouse stock received but not yet issued to a job.",
  }),
  acct("1900", "Other Assets", "asset", "other_asset", "debit"),

  // ───── Liabilities (2000s) ─────
  acct("2000", "Accounts Payable", "liability", "accounts_payable", "credit", {
    requiresSubledger: "vendor",
  }),
  acct("2100", "Credit Card Payable — Amex", "liability", "credit_card_payable", "credit"),
  acct("2110", "Credit Card Payable — Visa", "liability", "credit_card_payable", "credit"),
  acct("2200", "Deferred Revenue (Customer Deposits)", "liability", "deferred_revenue", "credit", {
    requiresSubledger: "customer",
  }),
  acct("2150", "Goods Received Not Invoiced", "liability", "accrued_liability", "credit", {
    requiresSubledger: "vendor",
    description: "Receipts posted to WIP/Inventory awaiting matching vendor bill. Clears when the bill posts.",
  }),
  acct("2300", "Accrued Payroll", "liability", "accrued_liability", "credit"),
  acct("2310", "Accrued Sales Tax", "liability", "accrued_liability", "credit"),
  acct("2900", "Other Liabilities", "liability", "other_liability", "credit"),

  // ───── Equity (3000s) ─────
  acct("3000", "Owner Equity", "equity", "equity", "credit"),
  acct("3900", "Retained Earnings", "equity", "retained_earnings", "credit"),

  // ───── Revenue / COGS (parents) ─────
  acct("4000", "Revenue (parent)", "revenue", "revenue", "credit", {
    description: "Roll-up. Post to trade-level children, not directly here.",
  }),
  acct("4990", "Purchase Discounts Taken", "revenue", "revenue", "credit", {
    description: "Early-pay discounts captured from vendor payment terms (e.g. 2/10 net 30).",
  }),
  acct("4995", "Gain on Disposal of Assets", "revenue", "revenue", "credit", {
    description: "Cash proceeds in excess of an asset's net book value at disposal.",
  }),
  acct("5000", "Cost of Material (parent)", "expense", "cost_of_material", "debit"),
  acct("5500", "Cost of Labor (parent)", "expense", "cost_of_labor", "debit"),

  // ───── Trade-level children ─────
  ...tradeAccounts(),

  // ───── Operating Expenses (6000s) ─────
  acct("6000", "Payroll — Office", "expense", "operating_expense", "debit"),
  acct("6010", "Payroll Taxes & Burden", "expense", "operating_expense", "debit"),
  acct("6100", "Rent", "expense", "operating_expense", "debit"),
  acct("6110", "Utilities", "expense", "operating_expense", "debit"),
  acct("6200", "Vehicles & Fuel", "expense", "operating_expense", "debit"),
  acct("6300", "Marketing & Advertising", "expense", "operating_expense", "debit"),
  acct("6400", "Insurance", "expense", "operating_expense", "debit"),
  acct("6500", "Software & Subscriptions", "expense", "operating_expense", "debit"),
  acct("6600", "Meals & Entertainment", "expense", "operating_expense", "debit"),
  acct("6700", "Professional Fees", "expense", "operating_expense", "debit"),
  acct("6800", "Bank & CC Fees", "expense", "operating_expense", "debit"),
  acct("6900", "Other Operating Expense", "expense", "operating_expense", "debit"),
  acct("6920", "Depreciation Expense", "expense", "operating_expense", "debit", {
    description: "Monthly straight-line depreciation posted from Fixed Assets.",
  }),
  acct("6995", "Loss on Disposal of Assets", "expense", "operating_expense", "debit", {
    description: "Net book value not recovered through cash proceeds at asset disposal.",
  }),
];
